import type { RadarBubble, RadarZones } from '@/services/api/teacher'
import { lighten, darken } from '../utils/colors'
import { LRUCache } from '../utils/lru-cache'

/**
 * Bubble-Grössen-Config (bubblenew-v2): erweitert das bisherige
 * { min, max } um die Backend-Skala. 'area' = Fläche ∝ Lektionsanzahl.
 * Zusätzlich Admin-Radar-Feature-Flags (lesen nur RadarCanvas/AdminRadar).
 */
export interface RadarSizeConfig {
  min_bubble_size: number
  max_bubble_size: number
  bubble_size_scale?: string
  admin_date_fan_enabled?: boolean
  admin_fan_overlap_fallback?: boolean
  enable_bubble_aggregation?: boolean
}

/**
 * bubblenew-v3 (B2/A2): Fan nur im Admin-Radar, wenn Flag UND Aggregation aktiv.
 * Bei deaktivierter Aggregation liefert das Backend geclusterte Bubbles
 * (Multi-Datum, Cluster-Offsets) — der Fan-Pfad setzt (Lehrer, Datum)-Bubbles
 * voraus; ohne ihn greift das alte Layout inkl. Overlap-Auflösung.
 */
export function adminFanActive(
  radarType: 'teacher' | 'admin',
  config: RadarSizeConfig | null
): boolean {
  if (radarType !== 'admin') return false
  const fanFlag = config?.admin_date_fan_enabled ?? true
  const aggregation = config?.enable_bubble_aggregation ?? true
  return fanFlag && aggregation
}

/** Sichtbarkeitsregel Kollaps (E6): versteckt, ausser Panik bleibt. */
export function isAdminCollapsedHidden(
  bubble: Pick<RadarBubble, 'requester_teacher_id' | 'hours_until_lesson'>,
  collapsedIds: ReadonlySet<string>,
  panicThreshold: number
): boolean {
  return collapsedIds.has(bubble.requester_teacher_id) && bubble.hours_until_lesson > panicThreshold
}

/** ~3.1s inhale+exhale — fast breathing, not a blinker. */
export const BREATHE_PERIOD_MS = 500

export type AttentionKind = 'prep_requested' | 'director_action' | 'cancellation' | 'unread'

export const ATTENTION_RGB: Record<AttentionKind, { r: number; g: number; b: number }> = {
  prep_requested: { r: 239, g: 68, b: 68 },
  director_action: { r: 99, g: 102, b: 241 },
  cancellation: { r: 249, g: 115, b: 22 },
  unread: { r: 59, g: 130, b: 246 },
}

export function resolveAttention(bubble: Pick<RadarBubble, 'prep_requested' | 'director_action_pending' | 'cancellation_pending' | 'has_unread'>): AttentionKind | null {
  if (bubble.prep_requested) return 'prep_requested'
  if (bubble.director_action_pending) return 'director_action'
  if (bubble.cancellation_pending) return 'cancellation'
  if (bubble.has_unread) return 'unread'
  return null
}

export function breatheScale(timestamp: number, periodMs = BREATHE_PERIOD_MS, amplitude = 0.08): number {
  return 1 + Math.sin(timestamp / periodMs) * amplitude
}

export function breatheAlpha(timestamp: number, periodMs = BREATHE_PERIOD_MS, mid = 0.55, amp = 0.25): number {
  return mid + Math.sin(timestamp / periodMs) * amp
}

// ============================================================================
// frost-v2 — Frost-Überzug V4 (Direktor-Gate sichtbar machen)
// Passive Stufe: liegt AUF der Bubble, koexistiert mit den atmenden
// Attention-Ringen (E4). Nur outgoing/admin (E1), nur offene Rollen
// pending/interested (E6), niemals director_returned (E2).
// ============================================================================

/** Schimmer-Zyklus des Frost-Schleiers (E10). */
export const FROST_SHIMMER_PERIOD_MS = 4000

export const FROST_VEIL_INNER = 'rgba(224,242,254,0.55)'
export const FROST_VEIL_MID = 'rgba(186,230,253,0.25)'
export const FROST_VEIL_OUTER = 'rgba(240,249,255,0.50)'
export const FROST_EDGE = '#bfdbfe'
export const FROST_RING = 'rgba(125,211,252,0.75)'
export const FROST_GLYPH = '#0c4a6e'
export const FROST_GLOW = 'rgba(125,211,252,0.5)'
export const FROST_MELT_DURATION_MS = 600

/** True, wenn diese Bubble den Frost-Überzug tragen soll (E1 defensiv). */
export function shouldFrost(b: Pick<RadarBubble, 'gate_pending' | 'bubble_type'>): boolean {
  return b.gate_pending === true && b.bubble_type !== 'incoming'
}

/**
 * frost-v3 (F1): Frost ist aktiv, wenn das Gate-Flag gesetzt ist ODER gerade
 * geschmolzen wird (alpha im Abklingen 0 < alpha < 1).
 *
 * Wichtig: frostAlpha hat den Normalwert 1 — «frostAlpha > 0» allein wäre
 * deshalb KEIN Frost-Indikator (frost-v2-Bug F1: alle Bubbles frosten).
 * Erster Melt-Frame bei elapsed exakt 0 (alpha=1.0) fehlt bewusst (F3, ~16ms).
 */
export function isFrostActive(bubble: Pick<RadarBubble, 'gate_pending' | 'bubble_type'>, frostAlpha: number): boolean {
  if (frostAlpha <= 0) return false
  // E1 defensiv: Incoming frosten nie — auch kein Schmelz-Overlay.
  if (bubble.bubble_type === 'incoming') return false
  return shouldFrost(bubble) || frostAlpha < 1
}

/**
 * Frost-Schimmer: langsames, subtiles Oszillieren des Schleier-Alphas zwischen
 * 0.9 und 1.0 (E10). Achtung: eigene Perioden-Formel — breatheAlpha nutzt
 * sin(ts/periodMs), was bei 4000 eine ~25s-Periode ergaebe (frost-v1-Bug B1).
 * Statisch (1.0) bei reduced motion erfolgt im Renderer.
 */
export function frostShimmer(timestamp: number): number {
  return 0.95 + Math.sin((timestamp * Math.PI * 2) / FROST_SHIMMER_PERIOD_MS) * 0.05
}

/**
 * E14 Schmelz-Übergang: Frost true→false zwischen zwei Datenständen startet
 * das einmalige Abklingen. prev === undefined (Erst-Render/Reload) schmilzt nie.
 */
export function shouldStartMelt(prev: boolean | undefined, now: boolean): boolean {
  return prev === true && now === false
}

/** Lineare Abkling-Alpha (1→0) über FROST_MELT_DURATION_MS; reduced motion = sofort weg. */
export function meltAlphaFromElapsed(elapsedMs: number, reducedMotion: boolean, durationMs = FROST_MELT_DURATION_MS): number {
  if (reducedMotion || !Number.isFinite(elapsedMs) || elapsedMs >= durationMs) return 0
  // RAF-timestamp kann kurz hinter performance.now() liegen — negatives
  // elapsed als Start (alpha=1) behandeln, nicht als «schon geschmolzen».
  if (elapsedMs < 0) return 1
  return 1 - elapsedMs / durationMs
}

// LRU cache for bubble positions (max 1000 entries)
const positionCache = new LRUCache<string, { x: number; y: number }>(1000)

// Generate cache key for position
// Uses rounded values to avoid floating point precision issues
function getPositionCacheKey(
  bubbleId: string,
  centerX: number,
  centerY: number,
  maxRadius: number,
  hoursUntilLesson: number,
  maxHours: number,
  panicThreshold: number,
  middleThreshold: number,
  angleDegrees: number
): string {
  const roundedX = Math.round(centerX)
  const roundedY = Math.round(centerY)
  const roundedR = Math.round(maxRadius)
  return `${bubbleId}:${roundedX}:${roundedY}:${roundedR}:h${hoursUntilLesson.toFixed(1)}:m${maxHours}:p${panicThreshold}:mid${middleThreshold}:a${angleDegrees.toFixed(1)}`
}

/**
 * Calculate zone radii based on backend thresholds
 * Returns ratios (0-1) for panic, middle, and outer zone boundaries
 * 
 * The radar is divided into three zones based on backend-configurable thresholds:
 * - Panic zone: 0 to panic_threshold hours (e.g., 0-2h)
 * - Middle zone: panic_threshold to middle_threshold hours (e.g., 2-48h)
 * - Outer zone: middle_threshold to max_hours (e.g., 48h-336h)
 * 
 * Zone radii are calculated dynamically based on thresholds to ensure
 * proportional visual representation with minimum visual sizes.
 * 
 * Includes validation to handle invalid backend configurations gracefully.
 */
export function calculateZoneRadii(
  zones: RadarZones,
  maxHours: number
): {
  panicInner: number
  panicOuter: number
  middleInner: number
  middleOuter: number
  outerInner: number
  outerOuter: number
} {
  let panicThreshold = zones.panic_zone.threshold_hours ?? 2
  let middleThreshold = zones.middle_zone.threshold_hours ?? 48
  
  // Validate thresholds
  if (panicThreshold >= middleThreshold) {
    console.warn('[Radar] Invalid zone config: panic_threshold >= middle_threshold. Using defaults.')
    panicThreshold = 2
    middleThreshold = 48
  }
  
  if (panicThreshold <= 0) {
    console.warn('[Radar] Invalid zone config: panic_threshold <= 0. Using default.')
    panicThreshold = 2
  }
  
  const effectiveMaxHours = Math.max(maxHours, middleThreshold + 1)
  
  // Calculate ratios based on thresholds
  const panicRatio = panicThreshold / effectiveMaxHours
  const middleRatio = middleThreshold / effectiveMaxHours
  
  // Ensure minimum visual sizes for zones
  const minPanicOuter = 0.15  // Minimum 15% for panic zone
  const minMiddleOuter = 0.40 // Minimum 40% for middle zone
  
  const panicOuter = Math.max(minPanicOuter, panicRatio * 0.5)
  const middleOuter = Math.max(minMiddleOuter, middleRatio * 0.7)
  
  return {
    panicInner: 0,
    panicOuter: panicOuter,
    middleInner: panicOuter,
    middleOuter: middleOuter,
    outerInner: middleOuter,
    outerOuter: 1.0,
  }
}

interface AnimatedBubble extends RadarBubble {
  floatPhase: number
  floatSpeed: number
}

export interface RenderPosition {
  x: number
  y: number
  radius: number
}

/**
 * Draw a single animated bubble
 * 4-layer rendering:
 * 1. Outer glow (shadow)
 * 2. Glass body (gradient fill)
 * 3. Inner highlight (simulated light source)
 * 4. Core/resolved indicator
 *
 * Phase 2.1: Added focus indicator for keyboard navigation
 * Phase 4.2: Returns RenderPosition instead of caching
 * Phase radar-v2: Added zones and maxHours parameters for zone-based positioning
 */
export function drawAnimatedBubble(
  ctx: CanvasRenderingContext2D,
  bubble: AnimatedBubble,
  timestamp: number,
  centerX: number,
  centerY: number,
  maxRadius: number,
  config: RadarSizeConfig | null,
  isHovered: boolean,
  isDimmed: boolean,
  reducedMotion: boolean,
  isFocused: boolean = false,
  zones: RadarZones,
  maxHours: number,
  tangentialOffset: number = 0,
  fanAngleDeg?: number,
  radialOverridePx?: number,
  isPinned: boolean = false,
  frostAlpha: number = 1
): RenderPosition {
  // Calculate position
  // bubblenew-v2: fanAngleDeg/radialOverridePx kommen aus dem Admin-Fan-Layout
  // (computeAdminFanLayout) und umgehen Position + Cache (direkte Berechnung).
  let posX: number
  let posY: number
  if (fanAngleDeg !== undefined || radialOverridePx !== undefined) {
    const angle = fanAngleDeg ?? bubble.angle_degrees
    const radial = radialOverridePx ?? calculateRadialDistance(bubble, zones, maxHours, maxRadius)
    const angleRad = ((angle - 90) * Math.PI) / 180
    posX = centerX + radial * Math.cos(angleRad)
    posY = centerY + radial * Math.sin(angleRad)
  } else {
    const pos = calculateBubblePosition(bubble, centerX, centerY, maxRadius, zones, maxHours)
    posX = pos.x
    posY = pos.y
  }
  // Tangentialer (seitlicher) Versatz fuer 3-Wege-Split-Bubbles: verschiebt die
  // Bubble entlang der Tangente (senkrecht zum Strahl), so dass die Rollen einer
  // Anfrage nebeneinander statt uebereinander liegen. Pixel-basiert -> unabhaengig
  // vom Radius zuverlaessig. Siehe computeSplitOffsets in RadarCanvas.
  // bubblenew-v2: Tangente am effektiven Winkel (Fan-Slot im Admin-Radar).
  if (tangentialOffset !== 0) {
    const effectiveAngleDeg = fanAngleDeg ?? bubble.angle_degrees
    const angleRad = ((effectiveAngleDeg - 90) * Math.PI) / 180
    posX += tangentialOffset * -Math.sin(angleRad)
    posY += tangentialOffset * Math.cos(angleRad)
  }

  // Calculate floating offset (only for bubbles NOT at center)
  // Bubbles at center (0h) must remain stable at the NOW marker
  // Bubbles at center are partially covered by NOW label (z-index: 50).
  // Label background (rgba(255, 255, 255, 0.85)) provides visual separation.
  const hours = Math.max(0, bubble.hours_until_lesson)
  const floatY = reducedMotion || hours === 0
    ? 0
    : Math.sin((timestamp / 1000) * bubble.floatSpeed + bubble.floatPhase) * 4

  const renderY = posY + floatY

  const radius = calculateVisualRadius(bubble.bubble_radius, maxRadius, config, isHovered)

  // DECLARE renderPos - was missing!
  const renderPos: RenderPosition = { x: posX, y: renderY, radius }

  const isIncoming = bubble.bubble_type === 'incoming'
  const isResolved =
    bubble.status === 'accepted' ||
    bubble.status === 'filled' ||
    bubble.status === 'booked'

  // frost-v3 (F1): Frost aktiv via isFrostActive — Gate-Flag ODER aktives
  // Schmelzen (E14: frostAlpha klingt 1→0 ab, obwohl gate_pending schon false).
  const frostActive = isFrostActive(bubble, frostAlpha)

  // Determine glow color based on urgency (using backend thresholds)
  const glowColor = getGlowColor(bubble, zones)

  ctx.save()

  // Apply dimming effect when another bubble is hovered
  if (isDimmed) {
    ctx.globalAlpha = 0.4
  }

  // ============================================================================
  // FOCUS INDICATOR - Draw first, behind everything
  // ============================================================================
  if (isFocused) {
    const focusPadding = Math.max(4, radius * 0.1)
    const pulseScale = reducedMotion ? 1 : 1 + Math.sin(timestamp / 300) * 0.05
    const focusRadius = (radius + focusPadding) * pulseScale

    ctx.beginPath()
    ctx.arc(posX, renderY, focusRadius, 0, Math.PI * 2)
    ctx.strokeStyle = '#3b82f6'
    ctx.lineWidth = 3
    ctx.setLineDash([5, 5])
    ctx.stroke()

    ctx.setLineDash([])

    ctx.shadowColor = '#3b82f6'
    ctx.shadowBlur = 20

    ctx.beginPath()
    ctx.arc(posX, renderY, radius + focusPadding + 4, 0, Math.PI * 2)
    ctx.strokeStyle = 'rgba(59, 130, 246, 0.3)'
    ctx.lineWidth = 2
    ctx.stroke()

    ctx.shadowBlur = 0
  }

  // ============================================================================
  // PIN INDICATOR (bubblenew-v2) - Panic-Bubble eines kollabierten Lehrers
  // ============================================================================
  if (isPinned) {
    ctx.save()
    ctx.beginPath()
    ctx.arc(posX, renderY, radius + 5, 0, Math.PI * 2)
    ctx.strokeStyle = 'rgba(185, 28, 28, 0.75)'
    ctx.lineWidth = 2
    ctx.setLineDash([4, 3])
    ctx.stroke()
    ctx.setLineDash([])
    ctx.restore()
  }

  // ============================================================================
  // Layer 1: Outer Glow (shadow)
  // ============================================================================
  if (isHovered) {
    ctx.shadowColor = '#3b82f6'
    ctx.shadowBlur = Math.min(25, radius * 0.6)
  } else if (frostActive) {
    // frost-v2 (E8): Dringlichkeits-Glow (Panic rot / Middle orange) dimmen —
    // halbierter Blur, Glow-Farbe Richtung eisblau mischen.
    ctx.shadowColor = FROST_GLOW
    ctx.shadowBlur = Math.min(isIncoming ? 18 : 12, radius * 0.5) * 0.5
  } else {
    ctx.shadowColor = glowColor
    ctx.shadowBlur = Math.min(isIncoming ? 18 : 12, radius * 0.5)
  }

  // ============================================================================
  // Layer 2: Glass Body (main circle)
  // ============================================================================
  ctx.beginPath()
  ctx.arc(posX, renderY, radius, 0, Math.PI * 2)

  if (isIncoming) {
    // Incoming bubbles: White fill with colored border
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)'
    ctx.fill()

    // Reset shadow for stroke
    ctx.shadowBlur = 0

    // Colored border (thicker for incoming)
    ctx.strokeStyle = bubble.bubble_color
    ctx.lineWidth = 3
    ctx.stroke()

    // Inner glass fill (subtle)
    ctx.beginPath()
    ctx.arc(posX, renderY, radius * 0.9, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)'
    ctx.fill()
  } else {
    // Outgoing bubbles: Gradient fill for depth
    const gradient = ctx.createRadialGradient(
      posX - radius * 0.3,
      renderY - radius * 0.3,
      0,
      posX,
      renderY,
      radius
    )

    // Create gradient from light (top-left) to dark (bottom-right)
    gradient.addColorStop(0, lighten(bubble.bubble_color, 35))
    gradient.addColorStop(0.4, bubble.bubble_color)
    gradient.addColorStop(1, darken(bubble.bubble_color, 15))

    ctx.fillStyle = gradient
    ctx.fill()

    // Reset shadow
    ctx.shadowBlur = 0
  }

  // ============================================================================
  // Layer 3: Inner Highlight (simulated light reflection)
  // ============================================================================
  if (!isIncoming) {
    const highlightOffset = Math.min(radius * 0.35, 12)
    const highlightRadius = Math.min(radius * 0.25, 10)

    ctx.beginPath()
    ctx.arc(
      posX - highlightOffset,
      renderY - highlightOffset,
      highlightRadius,
      0,
      Math.PI * 2
    )
    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)'
    ctx.fill()

    const secondaryOffset = Math.min(radius * 0.2, 8)
    const secondaryRadius = Math.min(radius * 0.1, 4)
    ctx.beginPath()
    ctx.arc(
      posX + secondaryOffset,
      renderY + secondaryOffset,
      secondaryRadius,
      0,
      Math.PI * 2
    )
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)'
    ctx.fill()
  } else {
    ctx.beginPath()
    ctx.arc(posX, renderY, Math.min(radius * 0.5, 20), 0, Math.PI * 2)
    ctx.fillStyle = `${bubble.bubble_color}20`
    ctx.fill()
  }

  // ============================================================================
  // frost-v2 — Frost-Überzug (F1–F4), passive Stufe ÜBER der Statusfarbe,
  // UNTER Badge/Hover (E4: atmende Attention-Ringe koexistieren und liegen
  // aussen). frostAlpha klinkt beim Schmelzen ab (E14).
  // ============================================================================
  if (frostActive) {
    // E10: langsames Schimmern 0.9–1.0 (~4s), statisch bei reduced motion.
    const shimmer = reducedMotion ? 1 : frostShimmer(timestamp)
    const a = Math.max(0, Math.min(1, frostAlpha * shimmer))

    // F1: Eisiger Schleier (radialer Gradient über der Statusfarbe)
    const veil = ctx.createRadialGradient(
      posX - radius * 0.25,
      renderY - radius * 0.25,
      radius * 0.1,
      posX,
      renderY,
      radius
    )
    veil.addColorStop(0, FROST_VEIL_INNER)
    veil.addColorStop(0.55, FROST_VEIL_MID)
    veil.addColorStop(1, FROST_VEIL_OUTER)
    ctx.save()
    ctx.globalAlpha = ctx.globalAlpha * a
    ctx.beginPath()
    ctx.arc(posX, renderY, radius, 0, Math.PI * 2)
    ctx.fillStyle = veil
    ctx.fill()

    // F2: Eisrand
    ctx.beginPath()
    ctx.arc(posX, renderY, radius, 0, Math.PI * 2)
    ctx.strokeStyle = FROST_EDGE
    ctx.lineWidth = Math.max(2, Math.min(2.5, radius * 0.1))
    ctx.stroke()

    // F4: ❄-Glyphe mittig — immer, auch auf Mini-Bubbles (E7), skaliert mit Radius
    const glyphSize = Math.max(1, Math.max(7, radius * 0.55) * frostAlpha)
    ctx.font = `bold ${glyphSize}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.shadowColor = 'white'
    ctx.shadowBlur = 2
    ctx.fillStyle = FROST_GLYPH
    ctx.fillText('❄', posX, renderY)
    ctx.shadowBlur = 0
    ctx.restore()

    // F3: Äusserer Frost-Ring — eng an der Bubble (Radius+3/+5), damit er nicht
    // mit dem atmenden Attention-Ring (Radius+max(8, r*0.15)) kollidiert (frost-v1 B3).
    const frostRingGap = radius >= 12 ? 5 : 3
    ctx.save()
    ctx.globalAlpha = ctx.globalAlpha * a
    ctx.beginPath()
    ctx.arc(posX, renderY, radius + frostRingGap, 0, Math.PI * 2)
    ctx.strokeStyle = FROST_RING
    ctx.lineWidth = 2
    ctx.setLineDash([6, 4])
    ctx.stroke()
    ctx.setLineDash([])
    ctx.restore()
  }

  // ============================================================================
  // Attention ring: one slow breathe, color by priority.
  // prep_requested > director_action > cancellation > unread
  // prep_open (notes exist, no action) stays a dashed amber ring only when idle.
  // ============================================================================
  const attention = resolveAttention(bubble)
  if (attention) {
    const rgb = ATTENTION_RGB[attention]
    const ringOffset = Math.max(8, radius * 0.15)
    if (reducedMotion) {
      ctx.beginPath()
      ctx.arc(posX, renderY, radius + ringOffset, 0, Math.PI * 2)
      ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8)`
      ctx.lineWidth = 3
      ctx.stroke()
    } else {
      const scale = breatheScale(timestamp)
      const alpha = breatheAlpha(timestamp)
      ctx.beginPath()
      ctx.arc(posX, renderY, radius * scale + ringOffset, 0, Math.PI * 2)
      ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
      ctx.lineWidth = 3
      ctx.shadowColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha * 0.7})`
      ctx.shadowBlur = Math.min(10, radius * 0.25)
      ctx.stroke()
      ctx.shadowBlur = 0
    }
  } else if (bubble.prep_open) {
    const prepOpenOffset = Math.max(4, radius * 0.1)
    if (reducedMotion) {
      ctx.beginPath()
      ctx.arc(posX, renderY, radius + prepOpenOffset, 0, Math.PI * 2)
      ctx.strokeStyle = 'rgba(245, 158, 11, 0.6)'
      ctx.lineWidth = 2.5
      ctx.setLineDash([4, 3])
      ctx.stroke()
      ctx.setLineDash([])
    } else {
      const prepRingRadius = (radius + prepOpenOffset) * breatheScale(timestamp, BREATHE_PERIOD_MS, 0.06)
      ctx.beginPath()
      ctx.arc(posX, renderY, prepRingRadius, 0, Math.PI * 2)
      ctx.strokeStyle = 'rgba(245, 158, 11, 0.6)'
      ctx.lineWidth = 2.5
      ctx.setLineDash([4, 3])
      ctx.stroke()
      ctx.setLineDash([])
    }
  }

  // ============================================================================
  // Layer 4: Resolved Indicator (white dot center)
  // frost-v2 (B4): unter Frost unterdrückt — ❄-Glyphe und Resolved-Dot würden
  // sich im Zentrum überlagern (nur relevant bei Misch-Bubbles ohne Splitting, A1).
  // ============================================================================
  if (isResolved && !frostActive) {
    const resolvedOuterRadius = Math.max(radius * 0.35, 6)
    const resolvedInnerRadius = Math.max(radius * 0.15, 3)

    ctx.beginPath()
    ctx.arc(posX, renderY, resolvedOuterRadius, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)'
    ctx.fill()

    ctx.beginPath()
    ctx.arc(posX, renderY, resolvedInnerRadius, 0, Math.PI * 2)
    ctx.fillStyle = bubble.bubble_color
    ctx.fill()
  }

  // ============================================================================
  // Layer 5: Lesson Count Badge (for grouped/aggregated bubbles)
  // ============================================================================
  if (bubble.lesson_count > 1) {
    const badgeRadius = calculateBadgeRadius(radius, false)
    const badgeOffset = calculateBadgeOffset(radius)
    const badgeX = posX + badgeOffset
    const badgeY = renderY - badgeOffset
    const badgeText = bubble.lesson_count.toString()

    ctx.beginPath()
    ctx.arc(badgeX, badgeY, badgeRadius, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(59, 130, 246, 0.9)'
    ctx.fill()

    ctx.strokeStyle = 'white'
    ctx.lineWidth = 2
    ctx.stroke()

    const fontSize = calculateBadgeFontSize(badgeRadius, badgeText, false)
    ctx.fillStyle = 'white'
    ctx.font = `bold ${fontSize}px sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(badgeText, badgeX, badgeY)
  }

  // ============================================================================
  // Hover Enhancement Ring
  // ============================================================================
  if (isHovered) {
    const hoverPadding = Math.max(4, radius * 0.1)
    ctx.beginPath()
    ctx.arc(posX, renderY, radius + hoverPadding, 0, Math.PI * 2)
    ctx.strokeStyle = '#3b82f6'
    ctx.lineWidth = 2
    ctx.shadowColor = '#3b82f6'
    ctx.shadowBlur = Math.min(10, radius * 0.3)
    ctx.stroke()
    ctx.shadowBlur = 0
  }

  ctx.restore()

  // Return render position for hit testing
  return renderPos
}

/**
 * Calculate the radial distance (px from center) of a bubble using
 * zone-based interpolation. Shared by calculateBubblePosition and the
 * same-ray overlap resolution, so both use identical distances.
 */
export function calculateRadialDistance(
  bubble: RadarBubble,
  zones: RadarZones,
  maxHours: number,
  maxRadius: number,
  radialCap?: number
): number {
  // Clamp hours to non-negative values
  const hours = Math.max(0, bubble.hours_until_lesson)

  // Get thresholds from backend config
  const panicThreshold = zones.panic_zone.threshold_hours ?? 2
  const middleThreshold = zones.middle_zone.threshold_hours ?? 48
  const effectiveMaxHours = Math.max(maxHours, middleThreshold + 1)

  const zoneRadii = calculateZoneRadii(zones, effectiveMaxHours)

  let radial: number
  if (hours <= panicThreshold) {
    // Panic zone: interpolate between panicInner and panicOuter
    const zoneProgress = panicThreshold > 0 ? hours / panicThreshold : 0
    radial = maxRadius * (zoneRadii.panicInner + zoneProgress * (zoneRadii.panicOuter - zoneRadii.panicInner))
  } else if (hours <= middleThreshold) {
    // Middle zone: interpolate between middleInner and middleOuter
    const zoneProgress = middleThreshold > panicThreshold
      ? (hours - panicThreshold) / (middleThreshold - panicThreshold)
      : 0
    radial = maxRadius * (zoneRadii.middleInner + zoneProgress * (zoneRadii.middleOuter - zoneRadii.middleInner))
  } else {
    // Outer zone: interpolate between outerInner and outerOuter
    const zoneProgress = Math.min(1, (hours - middleThreshold) / (effectiveMaxHours - middleThreshold))
    radial = maxRadius * (zoneRadii.outerInner + zoneProgress * (zoneRadii.outerOuter - zoneRadii.outerInner))
  }

  // bubblenew-v2 (T3.2): Optionaler Radial-Cap (Admin-Radar: maxRadius·0.88),
  // damit Platz für den Badge-Ring bleibt (Badges ausserhalb der Disk).
  if (radialCap !== undefined && radial > radialCap) {
    radial = radialCap
  }
  return radial
}

/**
 * Admin-Radar Datums-Fächer (bubblenew-v2, T3.1).
 *
 * Bubbles einer Lehrperson teilen sich backendseitig EINEN Winkel (angle_degrees
 * = Strahl des Lehrers). Diese Funktion verteilt die DATEN der Lehrperson auf
 * feste Winkelschlitzen um den Strahl (früh links → spät rechts) und deckelt
 * den Radius. Ein Mindest-Radialabstand (Perlenketten-Nudge) löst Rest-
 * Kollisionen auf, wenn die Zonen-Interpolation nahe Daten radial komprimiert.
 *
 * - Deterministisch (reine Funktion, Sortierung nach ISO-Datum).
 * - Split-Rollen desselben Tages erben den Slot-Winkel ihres Tages; ihre
 *   tangentiale Nebeneinander-Anordnung macht weiterhin computeSplitOffsets.
 * - slotDeg adaptiv: garantiert ~34px Tangentialabstand am äussersten Radius,
 *   gedeckelt auf ±24° Halbfächer (48°/n), damit Nachbar-Sektoren frei bleiben.
 *
 * Rückgabe: { angles: Map<bubbleId, fanAngleDeg>, radii: Map<bubbleId, cappedPx> }
 */
export interface AdminFanLayout {
  angles: Map<string, number>
  radii: Map<string, number>
}

export const ADMIN_FAN_RADIAL_CAP_RATIO = 0.88
/** Sicherheits-Padding zwischen benachbarten Slot-Bubbles. */
const ADMIN_FAN_SLOT_PADDING_PX = 4

export function computeAdminFanLayout(
  bubbles: RadarBubble[],
  zones: RadarZones,
  maxHours: number,
  maxRadius: number,
  config: RadarSizeConfig | null
): AdminFanLayout {
  const angles = new Map<string, number>()
  const radii = new Map<string, number>()
  const radialCap = maxRadius * ADMIN_FAN_RADIAL_CAP_RATIO

  // Lehrer -> Bubbles gruppieren
  const byTeacher = new Map<string, RadarBubble[]>()
  for (const b of bubbles) {
    if (!b.requester_teacher_id) continue
    const arr = byTeacher.get(b.requester_teacher_id) ?? []
    arr.push(b)
    byTeacher.set(b.requester_teacher_id, arr)
  }

  for (const teacherBubbles of byTeacher.values()) {
    const teacherAngle = teacherBubbles[0]?.angle_degrees ?? 0

    // Datum-Gruppen (früh → spät). lesson_date ist ISO (yyyy-mm-dd) → Stringsort = Zeitsortierung.
    const dateGroups = new Map<string, RadarBubble[]>()
    for (const b of teacherBubbles) {
      const key = b.lesson_date ?? ''
      const arr = dateGroups.get(key) ?? []
      arr.push(b)
      dateGroups.set(key, arr)
    }
    const dates = Array.from(dateGroups.keys()).sort()
    const n = dates.length

    // Radial-Basen je Datum (min hours der Gruppe = repreäsentative Distanz)
    const baseRadii = dates.map(d =>
      Math.min(...(dateGroups.get(d) ?? []).map(b =>
        calculateRadialDistance(b, zones, maxHours, maxRadius, radialCap)
      ))
    )
    const rMax = Math.max(...baseRadii, 1)

    // Visuelle Radien je Datum (grösste Bubble der Gruppe) — für die
    // grössenabhängige Slot-Breite und den Radial-Nudge.
    const visualRadii = dates.map(d =>
      Math.max(...(dateGroups.get(d) ?? []).map(b =>
        calculateVisualRadius(b.bubble_radius, maxRadius, config, false)
      ))
    )

    // bubblenew-v2 Fix: adaptive Slot-Breite aus den TATSÄCHLICHEN Bubble-Grössen.
    // Benachbarte Slots brauchen Tangentialabstand ≥ r_i + r_{i+1} + Padding;
    // konservativ am äussersten Radius gerechnet (Sehne bei Winkeldifferenz Δ:
    // 2·rMax·sin(Δ/2)). Gedeckelt auf 48°/n (Halbfächer ±24°), min 4°.
    let neededSlotDeg = 4
    if (n > 1) {
      for (let i = 1; i < n; i++) {
        const needPx = (visualRadii[i - 1] ?? 0) + (visualRadii[i] ?? 0) + ADMIN_FAN_SLOT_PADDING_PX
        const deltaDeg = 2 * ((Math.asin(Math.min(1, needPx / (2 * rMax))) * 180) / Math.PI)
        neededSlotDeg = Math.max(neededSlotDeg, deltaDeg)
      }
      neededSlotDeg = Math.min(neededSlotDeg, 48 / n)
    }
    const slotDeg = n > 1 ? neededSlotDeg : 0

    let prevR = 0
    let prevVisual = 0
    dates.forEach((d, i) => {
      const slotAngle = teacherAngle + (i - (n - 1) / 2) * slotDeg
      // Perlenketten-Nudge: nachfolgende Daten mindesten (r+v) weiter aussen;
      // wenn der Cap das verhindert, bleibt die Zonen-Position (Tangential-
      // separation übernimmt die Kollisionsfreiheit).
      let r = baseRadii[i] ?? 0
      const minRadial = prevVisual + (visualRadii[i] ?? 0) + ADMIN_FAN_SLOT_PADDING_PX
      if (i > 0 && r < prevR + minRadial) {
        const nudged = prevR + minRadial
        if (nudged <= radialCap) r = nudged
      }
      prevR = r
      prevVisual = visualRadii[i] ?? 0

      for (const b of dateGroups.get(d) ?? []) {
        angles.set(b.id, slotAngle)
        radii.set(b.id, r)
      }
    })
  }

  return { angles, radii }
}

/**
 * Calculate bubble position using zone-based interpolation.
 * 
 * The radar is divided into three zones based on backend-configurable thresholds:
 * - Panic zone: 0 to panic_threshold hours (e.g., 0-2h)
 * - Middle zone: panic_threshold to middle_threshold hours (e.g., 2-48h)
 * - Outer zone: middle_threshold to max_hours (e.g., 48h-336h)
 * 
 * Within each zone, the radial distance is interpolated based on hours_until_lesson.
 * This ensures temporal ordering: closer lessons appear closer to the center.
 * 
 * Zone radii are calculated dynamically based on thresholds to ensure
 * proportional visual representation.
 */
export function calculateBubblePosition(
  bubble: RadarBubble,
  centerX: number,
  centerY: number,
  maxRadius: number,
  zones: RadarZones,
  maxHours: number
): { x: number; y: number } {
  // Clamp hours to non-negative values
  const hours = Math.max(0, bubble.hours_until_lesson)
  
  // Get thresholds from backend config
  const panicThreshold = zones.panic_zone.threshold_hours ?? 2
  const middleThreshold = zones.middle_zone.threshold_hours ?? 48
  const effectiveMaxHours = Math.max(maxHours, middleThreshold + 1)
  
  // Check cache first
  const cacheKey = getPositionCacheKey(
    bubble.id,
    centerX,
    centerY,
    maxRadius,
    hours,
    effectiveMaxHours,
    panicThreshold,
    middleThreshold,
    bubble.angle_degrees
  )
  const cached = positionCache.get(cacheKey)
  if (cached) return cached

  const radialDistance = calculateRadialDistance(bubble, zones, maxHours, maxRadius)

  // Convert polar to cartesian
  const angleRad = ((bubble.angle_degrees - 90) * Math.PI) / 180
  
  let x = centerX + radialDistance * Math.cos(angleRad)
  let y = centerY + radialDistance * Math.sin(angleRad)

  // Apply cluster offset if applicable
  if (bubble.cluster_id && bubble.cluster_index > 0) {
    const offsetAngle = 15
    const direction = bubble.cluster_index % 2 === 0 ? 1 : -1
    const magnitude = (bubble.cluster_index + 1) / 2
    const adjustedAngleRad = ((bubble.angle_degrees + direction * offsetAngle * magnitude - 90) * Math.PI) / 180
    
    x = centerX + radialDistance * Math.cos(adjustedAngleRad)
    y = centerY + radialDistance * Math.sin(adjustedAngleRad)
  }

  const result = { x, y }
  positionCache.set(cacheKey, result)
  
  return result
}

/**
 * Clear the position cache
 * Call when bubbles change significantly (e.g., new data loaded)
 */
export function clearPositionCache(): void {
  positionCache.clear()
}

/**
 * Get glow color based on bubble urgency
 * Uses backend-configurable zone thresholds for consistency
 */
export function getGlowColor(bubble: RadarBubble, zones: RadarZones): string {
  const isResolved =
    bubble.status === 'accepted' ||
    bubble.status === 'filled' ||
    bubble.status === 'booked'

  if (isResolved) {
    return 'rgba(34, 197, 94, 0.4)' // Green glow for resolved
  }

  // Get thresholds from backend config
  const panicThreshold = zones.panic_zone.threshold_hours ?? 2
  const middleThreshold = zones.middle_zone.threshold_hours ?? 48

  if (bubble.hours_until_lesson <= panicThreshold) {
    return 'rgba(239, 68, 68, 0.6)' // Red glow for panic
  } else if (bubble.hours_until_lesson <= middleThreshold) {
    return 'rgba(249, 115, 22, 0.4)' // Orange glow for middle
  }

  // Default: use bubble's own color with reduced opacity
  return `${bubble.bubble_color}50`
}

/**
 * Calculate the visual pixel radius for a bubble.
 * Extracted for testability.
 *
 * Visual Radius Calculation:
 * - Uses config values (min_bubble_size, max_bubble_size) instead of magic numbers
 * - Applies responsive scaling based on canvas size (maxRadius / 250)
 * - Uses pow(x, 0.7) for better perceptual scaling (less compression than sqrt)
 * - Range: 12-44px (vs. old 10-30px) for clearer size differences
 * - Hover effect: +10% radius
 *
 * Formula:
 *   normalizedRadius = (bubbleRadius - minBubbleSize) / (maxBubbleSize - minBubbleSize)
 *   canvasScale = min(1, maxRadius / 250)
 *   minVisualRadius = 12 * canvasScale
 *   maxVisualRadius = 44 * canvasScale
 *   visualRange = maxVisualRadius - minVisualRadius
 *   scaledRadius = pow(clamp(normalizedRadius, 0, 1), 0.7)
 *   baseRadius = minVisualRadius + scaledRadius * visualRange
 *   return isHovered ? baseRadius * 1.1 : baseRadius
 */
export function calculateVisualRadius(
  bubbleRadius: number,
  maxRadius: number,
  config: RadarSizeConfig | null,
  isHovered: boolean
): number {
  const canvasScale = Math.min(1, maxRadius / 250)

  // bubblenew-v2 (E2): Area-Skala — px linear zum Backend-Wert (der ∝ √n ist),
  // OHNE die pow(x, 0.7)-Verzerrung und ohne min-Subtraktion. Nur so bleibt
  // die Flächen-Proportionalität (Ø(4) = 2·Ø(1)) auf Pixel-Ebene exakt.
  if (config && config.bubble_size_scale === 'area') {
    const backendMax = config.max_bubble_size || 80
    const maxVisual = 44 * canvasScale
    const px = maxVisual * Math.min(1, bubbleRadius / backendMax)
    const clamped = Math.max(12 * canvasScale, px)
    return isHovered ? clamped * 1.1 : clamped
  }

  const minBubbleSize = config?.min_bubble_size ?? 20
  const maxBubbleSize = config?.max_bubble_size ?? 80
  const sizeRange = maxBubbleSize - minBubbleSize
  const normalizedRadius = sizeRange > 0 ? (bubbleRadius - minBubbleSize) / sizeRange : 0

  const minVisualRadius = 12 * canvasScale
  const maxVisualRadius = 44 * canvasScale
  const visualRange = maxVisualRadius - minVisualRadius

  const clampedNorm = Math.max(0, Math.min(1, normalizedRadius))
  const scaledRadius = Math.pow(clampedNorm, 0.7)

  const baseRadius = config
    ? minVisualRadius + scaledRadius * visualRange
    : bubbleRadius / 2
  return isHovered ? baseRadius * 1.1 : baseRadius
}

/**
 * Compute tangential (side-by-side) pixel offsets for 3-Wege-Split-Bubbles.
 *
 * Split-Bubbles einer Anfrage (gleiche id-Gruppe `split_{key}_{value}_{role}`)
 * teilen sich Winkel + Radialabstand. Damit sie nebeneinander (nur minimal
 * ueberlappend) statt uebereinander liegen, werden sie entlang der Tangente
 * (senkrecht zum Strahl) als tangentiale Kreise ausgelegt.
 *
 * - Pixel-basiert (nutzt calculateVisualRadius), unabhaengig vom Radius zuverlaessig.
 * - Rolle-Reihenfolge links->rechts: declined, pending, filled.
 * - OVERLAP < 1.0 -> minimale Ueberlappung (1.0 = exakt tangential).
 *
 * Rueckgabe: Map<bubbleId, tangentialOffsetInPx>.
 */
export function computeSplitOffsets(
  bubbles: RadarBubble[],
  maxRadius: number,
  config: RadarSizeConfig | null
): Map<string, number> {
  const offsets = new Map<string, number>()
  const roleOrder: Record<string, number> = {
    declined: 0,
    cancelled: 1,
    expired: 2,
    assigned_elsewhere: 3,
    pending: 4,
    interested: 5,
    filled: 6,
  }
  const OVERLAP = 0.9 // 1.0 = tangential (keine Ueberlappung); <1 = minimale Ueberlappung
  const SPLIT_ROLE_SUFFIX = /_(filled|declined|pending|interested|assigned_elsewhere|cancelled|expired)$/

  const groups = new Map<string, { id: string; role: string; radius: number }[]>()
  for (const b of bubbles) {
    if (!b.is_split) continue
    const groupId = b.id.replace(SPLIT_ROLE_SUFFIX, '')
    const radius = calculateVisualRadius(b.bubble_radius, maxRadius, config, false)
    const arr = groups.get(groupId) ?? []
    arr.push({ id: b.id, role: b.split_role ?? 'pending', radius })
    groups.set(groupId, arr)
  }

  for (const arr of groups.values()) {
    arr.sort((a, b) => (roleOrder[a.role] ?? 9) - (roleOrder[b.role] ?? 9))
    // Zentren entlang der Linie; benachbarte Zentren im Abstand OVERLAP*(r_i+r_j).
    const centers: number[] = [0]
    for (let i = 1; i < arr.length; i++) {
      const prevCenter = centers[i - 1] ?? 0
      const prevRadius = arr[i - 1]?.radius ?? 0
      const curRadius = arr[i]?.radius ?? 0
      centers[i] = prevCenter + OVERLAP * (prevRadius + curRadius)
    }
    // Gruppe zentrieren (Mittelpunkt aus erstem/letztem Zentrum).
    const first = centers[0] ?? 0
    const last = centers[centers.length - 1] ?? 0
    const shift = -(first + last) / 2
    arr.forEach((b, i) => offsets.set(b.id, (centers[i] ?? 0) + shift))
  }

  return offsets
}

/**
 * Resolve pixel overlaps between bubbles on the same ray (same angle_degrees).
 *
 * Problem (Admin-Radar): mehrere Daten derselben Lehrperson liegen auf einem
 * Strahl. In der Aussen-Zone komprimiert die Zonen-Interpolation nahe Daten
 * auf wenige Pixel (z.B. 25.08. und 26.08. nur ~5px auseinander), wodurch die
 * kleineren Bubbles vollstaendig von groesseren verdeckt werden. Split-Bubbles
 * sind davon nicht betroffen (computeSplitOffsets).
 *
 * Loesung: Ueberlappende Bubbles eines Strahls werden entlang der Tangente
 * (senkrecht zum Strahl) nebeneinander gelegt — gleiche OVERLAP-Semantik wie
 * computeSplitOffsets (0.9 = minimale Ueberlappung).
 *
 * - initialOffsets: bestehende tangentiale Versaetze (Split-Bubbles) werden
 *   als Startposition uebernommen und nur bei echten Ueberlappungen erweitert.
 * - Determinismus: reine Funktion der Bubble-Daten (kein Zufall), damit die
 *   Versaetze frame-uebergreifend stabil bleiben.
 *
 * Rueckgabe: Map<bubbleId, tangentialOffsetInPx>.
 */
export function computeOverlapOffsets(
  bubbles: RadarBubble[],
  zones: RadarZones,
  maxHours: number,
  maxRadius: number,
  config: RadarSizeConfig | null,
  initialOffsets: Map<string, number> = new Map()
): Map<string, number> {
  const OVERLAP = 0.9 // 1.0 = tangential (keine Ueberlappung); <1 = minimale Ueberlappung
  const offsets = new Map<string, number>()

  // Bubbles auf demselben Strahl gruppieren (identischer Winkel).
  const rayGroups = new Map<number, RadarBubble[]>()
  for (const b of bubbles) {
    const angle = Math.round(b.angle_degrees * 100) / 100
    const arr = rayGroups.get(angle) ?? []
    arr.push(b)
    rayGroups.set(angle, arr)
  }

  for (const group of rayGroups.values()) {
    if (group.length < 2) continue

    // Innerste Bubble zuerst: nur nahe Nachbarn bekommen Versatz.
    const sorted = [...group].sort(
      (a, b) => calculateRadialDistance(a, zones, maxHours, maxRadius) - calculateRadialDistance(b, zones, maxHours, maxRadius)
    )

    const placed: { tangent: number; radial: number; radius: number }[] = []
    for (const b of sorted) {
      const radius = calculateVisualRadius(b.bubble_radius, maxRadius, config, false)
      const radial = calculateRadialDistance(b, zones, maxHours, maxRadius)
      const initTangent = initialOffsets.get(b.id) ?? 0

      // Verbotene Intervalle auf der Tangenten-Linie (Bereiche, in denen die
      // Bubble mit einer bereits platzierten kollidieren wuerde).
      const forbidden: [number, number][] = []
      for (const p of placed) {
        const minDist = OVERLAP * (radius + p.radius)
        const dr = radial - p.radial
        if (Math.abs(dr) >= minDist) continue
        const needed = Math.sqrt(minDist * minDist - dr * dr)
        forbidden.push([p.tangent - needed, p.tangent + needed])
      }

      let tangent = initTangent
      if (forbidden.length > 0) {
        // Kandidaten: 0/Startposition plus Intervall-Grenzen; waehle den mit
        // dem kleinsten |t|, der in keinem verbotenen Intervall liegt.
        const candidates = new Set<number>([0, initTangent])
        for (const [lo, hi] of forbidden) {
          candidates.add(lo)
          candidates.add(hi)
        }
        let bestFound = false
        for (const cand of [...candidates].sort((a, b) => Math.abs(a) - Math.abs(b))) {
          if (forbidden.some(([lo, hi]) => cand > lo && cand < hi)) continue
          tangent = cand
          bestFound = true
          break
        }
        if (!bestFound) {
          // Defensiv: die groesste rechte Intervall-Grenze liegt garantiert
          // ausserhalb aller Intervalle.
          tangent = Math.max(...forbidden.map(([, hi]) => hi))
        }
      }

      offsets.set(b.id, tangent)
      placed.push({ tangent, radial, radius })
    }
  }

  return offsets
}

/**
 * Calculate badge radius for lesson count indicator.
 * - Minimum 8px for readability
 * - Maximum 50% of bubble radius to avoid overflow
 * - Partial badges (e.g., "2/5") are 30% larger but capped
 */
export function calculateBadgeRadius(bubbleRadius: number, isPartial: boolean): number {
  const minBadgeRadius = 8
  const rawBadgeRadius = Math.max(bubbleRadius * 0.35, minBadgeRadius)
  const maxBadgeRadius = Math.max(bubbleRadius * 0.5, minBadgeRadius)
  const badgeRadius = Math.min(rawBadgeRadius, maxBadgeRadius)

  return isPartial
    ? Math.min(badgeRadius * 1.3, bubbleRadius * 0.55)
    : badgeRadius
}

/**
 * Calculate badge offset from bubble center.
 * Positions badge at upper-right corner of bubble.
 */
export function calculateBadgeOffset(bubbleRadius: number): number {
  return bubbleRadius * 0.55
}

/**
 * Calculate badge font size based on badge radius and text content.
 * - Partial badges use smaller font (0.8x) for "X/Y" format
 * - Multi-digit numbers use medium font (1.0x)
 * - Single digits use larger font (1.2x)
 */
export function calculateBadgeFontSize(badgeRadius: number, badgeText: string, isPartial: boolean): number {
  const isMultiDigit = badgeText.length > 1
  return badgeRadius * (isPartial ? 0.8 : isMultiDigit ? 1.0 : 1.2)
}
